/**
 * Admin Utilities
 * Centralized helpers for admin role detection and RPC-based operations.
 * All admin operations bypass RLS via SECURITY DEFINER RPCs.
 */

import { supabase } from "@/integrations/supabase/client";

// Session-cached admin status to avoid repeated RPC calls
let cachedAdminStatus: boolean | null = null;
let cachedUserId: string | null = null;

/**
 * Check if the current user is an admin.
 * Uses RPC (SECURITY DEFINER) to avoid RLS issues.
 * Caches result for the session (until user changes).
 */
export async function isAdminEffective(): Promise<boolean> {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    const currentUserId = user?.id ?? null;

    // If no user, not admin
    if (!currentUserId) {
      cachedAdminStatus = false;
      cachedUserId = null;
      return false;
    }

    // If same user and cached, return cached value
    if (cachedUserId === currentUserId && cachedAdminStatus !== null) {
      return cachedAdminStatus;
    }

    // Call RPC to check admin status
    const { data, error } = await (supabase.rpc as any)("current_user_is_admin");
    
    if (error) {
      console.warn("[isAdminEffective] RPC failed:", error);
      cachedAdminStatus = false;
    } else {
      cachedAdminStatus = Boolean(data);
    }
    
    cachedUserId = currentUserId;
    console.log("[ADMIN] isAdminEffective =", cachedAdminStatus);
    return cachedAdminStatus;
  } catch (err) {
    console.error("[isAdminEffective] unexpected error:", err);
    return false;
  }
}

/**
 * Clear the cached admin status (call on logout or user change)
 */
export function clearAdminCache(): void {
  cachedAdminStatus = null;
  cachedUserId = null;
}

// ============================================================================
// Admin RPC wrappers for protected table operations
// ============================================================================

/**
 * Fetch assessment overview using admin RPC
 */
export async function adminGetAssessmentOverview(assessmentId: string) {
  console.log("[ADMIN] RPC=admin_get_assessment_overview assessmentId=", assessmentId);
  const { data, error } = await supabase.rpc("admin_get_assessment_overview", {
    p_assessment_id: assessmentId,
  });
  if (error) throw error;
  return data?.[0] ?? null;
}

/**
 * Fetch responses using admin RPC
 * Uses the canonical admin_list_responses(uuid, integer[]) signature
 */
export async function adminListResponses(
  assessmentId: string,
  questionIds: number[] | null = null
) {
  const params: { p_assessment_id: string; p_question_ids: number[] | null } = {
    p_assessment_id: assessmentId,
    p_question_ids: questionIds && questionIds.length > 0 ? questionIds.map(Number) : null,
  };
  // Log before RPC call
  console.log("[admin_list_responses] payload", { 
    p_assessment_id: params.p_assessment_id, 
    p_question_ids: params.p_question_ids 
  });
  // Use canonical admin_list_responses RPC
  const { data, error } = await (supabase.rpc as any)("admin_list_responses", params);
  // Log after RPC call
  console.log("[admin_list_responses] result", { error, rows: data?.length });
  if (error) throw error;
  return data ?? [];
}

/**
 * Upsert responses using admin RPC (batch)
 */
export async function adminUpsertResponses(
  rows: Array<{
    assessment_id: string;
    question_id: number;
    score: number | null;
    comment?: string | null;
    evidence_links?: string[] | null;
  }>
) {
  console.log("[ADMIN] RPC=admin_upsert_responses count=", rows.length);
  const { error } = await (supabase.rpc as any)("admin_upsert_responses", {
    p_rows: rows,
  });
  if (error) throw error;
}

/**
 * Fetch questionnaire items using admin RPC
 */
export async function adminListQuestionnaireItems(questionnaireId: string) {
  console.log("[ADMIN] RPC=admin_list_questionnaire_items questionnaireId=", questionnaireId);
  const { data, error } = await (supabase.rpc as any)("admin_list_questionnaire_items", {
    p_questionnaire_id: questionnaireId,
  });
  if (error) throw error;
  return data ?? [];
}

/**
 * Fetch reviewer ratings using admin RPC
 */
export async function adminListReviewerRatings(assessmentId: string) {
  console.log("[ADMIN] RPC=admin_list_reviewer_ratings assessmentId=", assessmentId);
  const { data, error } = await (supabase.rpc as any)("admin_list_reviewer_ratings", {
    p_assessment_id: assessmentId,
  });
  if (error) throw error;
  return data ?? [];
}

/**
 * Fetch assessment assignees using admin RPC
 */
export async function adminListAssessmentAssignees(assessmentId: string) {
  console.log("[ADMIN] RPC=admin_list_assessment_assignees assessmentId=", assessmentId);
  const { data, error } = await (supabase.rpc as any)("admin_list_assessment_assignees", {
    p_assessment_id: assessmentId,
  });
  if (error) throw error;
  return data ?? [];
}

/**
 * Fetch org memberships using admin RPC
 */
export async function adminListOrgMemberships(portfolioId: string) {
  console.log("[ADMIN] RPC=admin_list_org_memberships portfolioId=", portfolioId);
  const { data, error } = await (supabase.rpc as any)("admin_list_org_memberships", {
    p_org_id: portfolioId,
  });
  if (error) throw error;
  return data ?? [];
}

/**
 * Assign user to portfolio using admin RPC
 */
export async function adminAssignUserToPortfolio(params: {
  portfolioId: string;
  email: string;
  roles: string[];
  name?: string | null;
  password?: string | null;
}) {
  console.log("[ADMIN] RPC=admin_assign_user_to_portfolio portfolioId=", params.portfolioId, "email=", params.email, "roles=", params.roles);
  const { data, error } = await supabase.rpc("admin_assign_user_to_portfolio", {
    p_portfolio_id: params.portfolioId,
    p_email: params.email,
    p_roles: params.roles,
    p_name: params.name ?? null,
    p_password: params.password ?? null,
  });
  if (error) throw error;
  return data;
}

/**
 * Fetch assessments for a portfolio using admin RPC
 */
export async function adminListAssessmentsForPortfolio(portfolioId: string) {
  console.log("[ADMIN] RPC=admin_list_assessments_by_org portfolioId=", portfolioId);
  const { data, error } = await supabase.rpc("admin_list_assessments_by_org", {
    p_org_id: portfolioId,
  });
  if (error) throw error;
  return data ?? [];
}

/**
 * Fetch single portfolio summary using admin RPC
 */
export async function adminGetPortfolioSummary(portfolioId: string) {
  console.log("[ADMIN] RPC=admin_get_portfolio_summary portfolioId=", portfolioId);
  const { data, error } = await (supabase.rpc as any)("admin_get_portfolio_summary", {
    p_portfolio_id: portfolioId,
  });
  if (error) throw error;
  // RPC returns TABLE, so data is array - take first row
  return data?.[0] ?? null;
}

/**
 * Fetch reviewer rating activity timestamps using admin RPC
 * Returns { latest_scored_at, latest_notes_at } or null
 */
export async function adminGetReviewerRatingActivity(assessmentId: string) {
  console.log("[ADMIN] RPC=admin_get_reviewer_rating_activity assessmentId=", assessmentId);
  const { data, error } = await (supabase.rpc as any)("admin_get_reviewer_rating_activity", {
    p_assessment_id: assessmentId,
  });
  if (error) {
    console.warn("[ADMIN] admin_get_reviewer_rating_activity error:", error);
    return null;
  }
  // RPC returns TABLE, so data is array - take first row
  return data?.[0] ?? null;
}

/**
 * Fetch assessment questionnaire_id using admin RPC
 * Returns { questionnaire_id } or null
 */
export async function adminGetAssessmentQuestionnaireId(assessmentId: string) {
  console.log("[ADMIN] RPC=admin_get_assessment_questionnaire_id assessmentId=", assessmentId);
  const { data, error } = await (supabase.rpc as any)("admin_get_assessment_questionnaire_id", {
    p_assessment_id: assessmentId,
  });
  if (error) {
    console.warn("[ADMIN] admin_get_assessment_questionnaire_id error:", error);
    return null;
  }
  // RPC returns TABLE, so data is array - take first row
  return data?.[0]?.questionnaire_id ?? null;
}

/**
 * Fetch questions by IDs using admin RPC
 * Returns array of { id, domain, question_key }
 */
export async function adminListQuestionsByIds(ids: number[]) {
  console.log("[ADMIN] RPC=admin_list_questions_by_ids count=", ids.length);
  const { data, error } = await (supabase.rpc as any)("admin_list_questions_by_ids", {
    p_ids: ids,
  });
  if (error) {
    console.warn("[ADMIN] admin_list_questions_by_ids error:", error);
    return [];
  }
  return data ?? [];
}